import {
  theme as chakraTheme,
  cssVar,
  extendTheme,
  StyleFunctionProps,
  withDefaultColorScheme,
} from '@chakra-ui/react';

import { Alert } from './components/Alert';
import { Button } from './components/Button';
import { Card } from './components/Card';
import { Checkbox } from './components/Checkbox';
import { DataTable } from './components/DataTable';
import { Input } from './components/Input';
import { Menu } from './components/Menu';
import { Progress } from './components/Progress';
import { Radio } from './components/Radio';
import { Select } from './components/Select';
import { Stack } from './components/Stack';
import { Table } from './components/Table';
import { Tag } from './components/Tag';
import { foundations } from './foundations';
import { radii } from './foundations/radius';
import { styles } from './styles';

const { colors, textStyles, fontSizes, space } = foundations;

export const NabisTheme = {
  ...foundations,
  components: {
    Alert,
    Checkbox,
    Card,
    Button,
    Badge: {
      baseStyle: (props: StyleFunctionProps) => {
        const { colorScheme } = props;
        let colorProps = chakraTheme.colors[colorScheme as keyof typeof chakraTheme.colors];
        if (!colorProps) {
          colorProps = chakraTheme.colors.gray;
        }

        return {
          ...textStyles.badge,
          borderColor: colorProps[200],
          borderWidth: '1px',
          borderStyle: 'solid',
          padding: space['1'],
        };
      },
      variants: {
        round: (props: StyleFunctionProps) => ({
          ...chakraTheme.components.Badge.variants?.subtle(props),
          padding: space['1.5'],
          border: 'none',
          borderRadius: radii.full,
        }),
        red: ({ colorMode }: StyleFunctionProps) => ({
          color: colors.accent.red.fg[colorMode],
          backgroundColor: colors.accent.red.bg[colorMode],
          borderColor: colors.border.transparent[colorMode],
          fontWeight: 500,
        }),
        orange: ({ colorMode }: StyleFunctionProps) => ({
          color: colors.accent.orange.fg[colorMode],
          backgroundColor: colors.accent.orange.bg[colorMode],
          borderColor: colors.border.transparent[colorMode],
          fontWeight: 500,
        }),
      },
    },
    Text: {
      baseStyle: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
        color: colors.text.base[colorMode],
        fontSize: '14px',
      }),
      variants: {
        secondary: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({ color: colors.text.secondary[colorMode] }),
        // To use: <Text variant="pageTitle">
        ...textStyles,
      },
    },
    Heading: {
      baseStyle: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
        color: colors.text.base[colorMode],
        fontSize: '14px',
        fontWeight: textStyles.pageTitle.fontWeight,
      }),
      variants: {
        // To use: <Heading variant="pageTitle">
        ...textStyles,
      },
    },
    Menu,
    Input,
    Select,
    Switch: {
      baseStyle: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
        track: {
          bg: colors.border.form.default[colorMode],

          _checked: { bg: colors.interactive.activate },

          _disabled: {
            opacity: '0.5',
            _dark: { opacity: '0.2' },
          },
        },
        // Is this color from the design? In the CA app we are using a different color scheme.
        // color: colors.text.base[colorMode],
        fontSize: '14px',
      }),
    },
    Textarea: {
      baseStyle: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
        baseStyle: {
          bg: colors.border.form.default[colorMode],
          px: '1rem',
          py: '0.75rem',
        },
      }),
    },
    Drawer: {
      baseStyle: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
        dialog: { bg: colors.background.global[colorMode] },
      }),
    },
    MainContainer: {
      baseStyle: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({ bg: colors.background.global[colorMode] }),
    },
    SubCard: {
      baseStyle: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
        bg: colors.background.nestedContainer[colorMode],
        width: '100%',
        padding: `${space['5']} ${space['4']}`,
        borderRadius: radii.md,
      }),
    },
    Popover: { baseStyle: { popper: { zIndex: 1500 } } },
    PopoverBody: { baseStyle: { baseStyle: { bg: colors.background.container.light } } },
    Progress,
    Radio,
    FormLabel: { baseStyle: { fontWeight: 'normal' }, variants: { ...textStyles } },
    Divider: {
      baseStyle: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
        borderColor: colors.border.transparent[colorMode],
      }),
    },
    Form: {
      baseStyle: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
        helperText: {
          marginTop: '-5px',
          marginBottom: space['2'],
          fontSize: fontSizes.sm,
          fontWeight: 'normal',
          color: colors.text.secondary[colorMode],
        },
        container: { marginBottom: space['4'] },
      }),
    },
    IconButton: { variants: { ghostTransparent: { _hover: { background: 'transparent' } } } },
    DataTable,
    Modal: {
      baseStyle: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
        closeButton: {
          fontSize: fontSizes.sm,
          top: 3,
          right: 3,
        },
        dialog: { borderRadius: radii['2xl'], overflow: 'hidden' },
        header: {
          bg: colors.background.container[colorMode],
          borderBottom: '1px solid',
          borderColor: colors.border.opaque[colorMode],
          fontSize: foundations.textStyles.sectionHeading.fontSize,
        },
        body: { bg: colors.background.container[colorMode] },
        footer: {
          bg: colors.background.container[colorMode],
          borderTop: '1px solid',
          borderColor: colors.border.opaque[colorMode],
        },
      }),
    },
    Stack,
    Table,
    Tag,
    Tooltip: {
      baseStyle: ({ colorMode }: { colorMode: 'dark' | 'light' }) => ({
        py: '0.5rem',
        px: '0.625rem',
        borderRadius: '0.25rem',
        fontSize: '0.75rem',
        fontWeight: '400',
        lineHeight: '1.35em',
        /**
         * TODO: These properties only apply to the light mode so update them when you know more
         * (not sure what dark mode is supposed to look like yet so leaving dark mode default as is)
         */
        color: colorMode === 'light' ? colors.text.white : undefined,
        // Background color for the tooltip (not including arrow)
        backgroundColor: colorMode === 'light' ? colors.background.container.dark : undefined,
        // Background color for the arrow
        [cssVar('popper-arrow-bg').variable]: colorMode === 'light' ? colors.background.container.dark : undefined,
      }),
    },
  },
  styles,
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
} as const;

export const theme = extendTheme(NabisTheme, withDefaultColorScheme({ colorScheme: 'brand' }));
